import { t } from '@citizen-walk/shared-i18n';
import {
  applyOutputFieldsOverride,
  resolveMainSession,
  formatMembershipProfileValue,
} from '@citizen-walk/shared-types';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnnouncementHistoryModal } from '../announcements';
import {
  DeviceTransferModal,
  RecoveryRecoverModal,
  SettingsModal,
  useAnonymousAuth,
  useMembershipUserFields,
  useMyIdentity,
  useNickname,
} from '../auth';
import { DistrictPendingScreen, useDistrict, useDistrictState } from '../district';
import { GisSettingsModal, useColorRule, useDistrictPmtiles } from '../map';
import { useCurrentSession, useMembershipSessions } from '../sessions';
import {
  AnalysisIdeaView,
  AnalysisInsightView,
  deriveDialogueContext,
  EcwOutboxList,
  IdeaDetailModal,
  IdeaWorkspace,
  InsightDetailModal,
  useEcwIdeas,
  useEcwInsights,
  useEcwOutbox,
  type IdeaMinimal,
  type InsightMinimal,
} from '../insights';
import {
  PhotoDownloadModal,
  PostDetailModal,
  resolvePostFields,
  useAiCategoryMasters,
  useAnalysisDisplayConfig,
  usePosts,
  type PostMinimal,
} from '../posts';
import { Drawer, DrawerToggleButton } from '../shell';
import { SurveyListModal } from '../survey';
import { VerificationListPage } from '../verification';
import { AnalysisHeatmapView } from './AnalysisHeatmapView';
import {
  AnalysisListView,
  type AnalysisDetailContext,
  type ProceedContext,
} from './AnalysisListView';
import { CategoryMasterTableModal } from './CategoryMasterTableModal';
import { AnalysisMapView } from './AnalysisMapView';
import { AnalysisMatrixView } from './AnalysisMatrixView';
import { AnalysisNetworkGraphView } from './AnalysisNetworkGraphView';
import { AnalysisScoreView } from './AnalysisScoreView';
import { useAnalysisPerspectives } from './analysisPerspectives';
import { useOutputFormat } from './useOutputFormat';

/**
 * 分析アプリ メインレイアウト (Sprint X3、旧 `AnalysisLayout.tsx` 643 行を minimum 範囲で踏襲)。
 *
 * 旧の 7 タブ (matrix / map / analysis / insights / ideas / network / heatmap) のうち、
 * Sprint X3 では **list (一覧) + matrix (マトリクス)** の 2 タブのみ実装。残りは Sprint X4 以降:
 *   - map: 既存 /map route + MapView を再利用予定 (Sprint X4 で /analysis 内 tab 統合検討)
 *   - insights / ideas: ECW idea 機能 (旧 InsightList / IdeaCardList / IdeaForm 移植、Sprint X4)
 *   - network: NetworkGraphView (旧 1533 行、Sprint X5)
 *   - heatmap: HeatmapView (旧 591 行、Sprint X4)
 *
 * 構成 (旧踏襲、UI inheritance policy):
 *   - ヘッダー (左上 ☰ + 地区名 + "分析ツール")
 *   - タブ nav (一覧 / マトリクス)
 *   - メインコンテンツ (タブ別 view を mount/unmount でなく display 切替で state 維持)
 *   - Drawer + 各 Modal は AppHome と同水準で AnalysisPage が所有
 *   - 選択 post は PostDetailModal で詳細表示 (Sprint X2 の M2/M5 連動 Renderer 再利用)
 *
 * 制約:
 *   - 認証必須 (useAnonymousAuth でサインイン状態 確認、未認証時は loading 表示)
 *   - district は DistrictProvider 配下前提。未解決中は末尾 gate で DistrictPendingScreen を表示
 *     (地区フォールバック根絶: 旧 PHASE2_FALLBACK_DISTRICT 動作は撤去)
 *   - usePosts の集計対象 districtId は district.id (本人 hidden_others を含む)
 */

type AnalysisTab =
  | 'list'
  | 'analysis'
  | 'matrix'
  | 'score'
  | 'map'
  | 'heatmap'
  | 'insights'
  | 'ideas'
  | 'network';

type TabDef = { key: AnalysisTab; label: string; icon: string; legacy?: boolean };

/**
 * docs/105 / 2026-05-30 ユーザー指摘: タブ順を old2 `AnalysisLayout` (L411-419) に揃える。
 * old2 順 = matrix → map → analysis → insights → ideas → network → heatmap
 *   - `analysis` (粗選択を受けた「詳細選択」タブ) は Phase 4 で新規追加予定。
 *   - ヒートマップ/ネットワークは「気づき・処方箋の後に見るもの」なので末尾。
 *
 * `list` / `score` は old2 に独立タブとして存在せず「邪魔」との指摘を受け既定非表示。
 *   - tab content 自体は残置し、SHOW_LEGACY_TABS=true で nav に再表示できる (設定フラグ)。
 *   - score の散布図は old2 では HeatmapView 内の一要素 (独立タブではない)。
 */
const SHOW_LEGACY_TABS = false;

// Sprint BG Part 3 — i18n sample 化。label は t() 経由で ja/en 切替可能 (default: ja)。
const ALL_TABS: TabDef[] = [
  { key: 'matrix', label: t('analysis.tab_matrix'), icon: '🔲' },
  // docs/105: 地図タブ。i18n キー (analysis.tab_map) は shared-i18n 側未追加 (別作業 scope) のため
  // 暫定でリテラルラベル。shared-i18n に key 追加後に t('analysis.tab_map') へ差し替え予定。
  { key: 'map', label: '地図', icon: '🗺️' },
  // Phase 4: 分析(詳細選択)タブ (old2 AnalysisView 相当)。matrix/map の粗選択を受けて
  // 詳細フィルタ + ✓キープ → 「気づき作成へ」で対話ワークスペースへ直行する。
  { key: 'analysis', label: '分析', icon: '🔍' },
  { key: 'insights', label: t('analysis.tab_insight'), icon: '💡' },
  { key: 'ideas', label: t('analysis.tab_idea'), icon: '💊' },
  { key: 'network', label: t('analysis.tab_network'), icon: '🕸️' },
  { key: 'heatmap', label: t('analysis.tab_heatmap'), icon: '🔥' },
  // 既定非表示 (old2 に独立タブ無し)。SHOW_LEGACY_TABS で再表示可。
  // 一覧 (list) は『分析』タブに統合済 (AnalysisListView を analysis タブとして再利用)。
  { key: 'score', label: t('analysis.tab_score'), icon: '📊', legacy: true },
];

const TABS: TabDef[] = ALL_TABS.filter((tab) => SHOW_LEGACY_TABS || !tab.legacy);

export function AnalysisPage() {
  const auth = useAnonymousAuth();
  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  const district = useDistrict();
  const districtId = district?.id ?? null;
  const districtState = useDistrictState();
  // 地区フォールバック根絶: 地区未解決のあいだ districtId は null。各 hook は no-op し、
  // ページ末尾の gate で DistrictPendingScreen を表示する (phase2-default 誤購読を防ぐ)。
  // R-70: 「自分」= 現 uid + 端末引き継ぎ元。docs/28: ぼかし前の写真を本人にだけ見せる判定にも使う。
  const identity = useMyIdentity(uid, districtId);
  const postsState = usePosts(uid, districtId, 0, identity.allUids);
  const posts = postsState.status === 'ready' ? postsState.posts : [];

  // docs/107 Wave1 / old2 AnalysisLayout.tsx:56-58,227-250 — 管理者プレビューリンク (?adminNickname=)。
  // 設定時は気づき・処方箋投稿の表示名をこの値で上書きする (membership nickname より優先)。
  // Router context に依存しないよう window.location.search から直読み (テスト環境でも安全)。
  const adminNicknameOverride = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('adminNickname');
    return v?.trim() || null;
  }, []);

  // docs/107 Wave1 / old2 AnalysisLayout.tsx:72-105,436-495 — セッションフィルタ。
  // 参加中セッション (joinedSessionIds) を既定選択、未参加なら全セッション。
  // sessionId=null (セッション未参加投稿) と孤児セッションID (当該地区に無いID) は常に表示。
  const sessionsCurrent = useCurrentSession(districtId);
  const allSessions = useMemo(
    () => (sessionsCurrent.state.status === 'ready' ? sessionsCurrent.state.items : []),
    [sessionsCurrent.state],
  );
  const { joinedSessionIds, mainSessionId } = useMembershipSessions(uid, districtId);
  // 気づき/処方箋にもユーザーのメインセッションを記録するため、投稿と同じ供給源
  // (membership.mainSessionId → resolveMainSession) で現在のメインセッション ID を解決する
  // (useMainSession と同じロジック。AnalysisPage は既に useCurrentSession+useMembershipSessions を
  //  使うため、二重購読を避けて inline で解決)。
  const currentMainSessionId = useMemo(
    () =>
      resolveMainSession(
        joinedSessionIds,
        mainSessionId,
        allSessions.map((s) => s.id),
      ),
    [joinedSessionIds, mainSessionId, allSessions],
  );
  // セッション ID → 名前 (気づき/処方箋カードの表示用、内部 key は出さず名前文字列で表示)。
  const sessionNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of allSessions) m.set(s.id, s.name);
    return m;
  }, [allSessions]);
  // docs/107 Wave3: 地図タブのオフライン pmtiles ベース用 (MapPage L109-110 と同 pattern)。
  const pmtilesState = useDistrictPmtiles(districtId);
  const pmtilesMeta = pmtilesState.status === 'ready' ? pmtilesState.meta : null;
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [sessionDropdownOpen, setSessionDropdownOpen] = useState(false);
  const sessionDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sessionInitialized) return;
    if (joinedSessionIds.length > 0) {
      setSelectedSessionIds(new Set(joinedSessionIds));
      setSessionInitialized(true);
    } else if (allSessions.length > 0 && posts.length > 0) {
      // 当該地区にまだ参加セッションが無い場合は全セッション表示にする
      setSelectedSessionIds(new Set(allSessions.map((s) => s.id)));
      setSessionInitialized(true);
    }
  }, [joinedSessionIds, allSessions, posts.length, sessionInitialized]);

  const filteredPosts = useMemo(() => {
    if (allSessions.length === 0 || selectedSessionIds.size === 0) return posts;
    const validSessionIds = new Set(allSessions.map((s) => s.id));
    return posts.filter((p) => {
      if (!p.sessionId) return true; // セッション未参加投稿は常に表示
      if (!validSessionIds.has(p.sessionId)) return true; // 孤児セッションIDも常に表示
      return selectedSessionIds.has(p.sessionId);
    });
  }, [posts, selectedSessionIds, allSessions]);

  // 管理画面で地区に設定した色分け (District.mapDisplay.colorRule)。投稿アプリの地図・一覧・詳細と
  // 同じ hook を使うので、分析地図で選んだときに色が完全に一致する。未設定の地区では null。
  const districtColorRule = useColorRule();

  // Sprint X2 の M2/M5 連動 hook を分析アプリでも共有 (R-36 / Sprint X4 で district 一次情報を使用)。
  // 旧: posts[0].aiAnalysisSettingsId fallback (複数 settings 混在で誤解釈リスク)。
  // 新: district.aiAnalysisSettingsId 直接使用、未設定 (Provider 外 / Phase 2 fallback) のみ
  //     posts[0] へ fallback (後方互換のため)。
  const settingsId =
    district?.aiAnalysisSettingsId ??
    posts.find((p) => p.aiAnalysisSettingsId)?.aiAnalysisSettingsId ??
    null;
  const resolver = useAiCategoryMasters(settingsId);

  // AI 対話の {{FACTS}} を「投稿者が入力した項目すべて」にするための項目定義 (地区ごとに異なる)。
  // IdeaWorkspace へ props で渡す (地区未解決の間は空 = 組込項目のみの fallback)。
  const postFields = useMemo(() => (district ? resolvePostFields(district) : []), [district]);

  // Sprint BG-DataSettings 18 (GAP U-L10 close): M3 出力フォーマット解決。
  // settingsId → M1 → outputFormatId → M3 の 2 step を hook 内で完結。
  // Matrix view が分類軸抽出を hardcode せず format-driven にする (U-L18 close)。
  // docs/29 §5-1: 既定の観点の地区の上書き (出力フォーマットの差し替え・この地区だけの出力項目) を反映する。
  const defaultAnalysisOption = district?.aiPostAnalyses?.find((o) => o.isDefault) ?? null;
  const { format: rawOutputFormat } = useOutputFormat(
    settingsId,
    defaultAnalysisOption?.outputFormatIdOverride ?? null,
  );
  const outputFormat = useMemo(() => {
    const schema = applyOutputFieldsOverride(
      rawOutputFormat?.schema ?? null,
      defaultAnalysisOption?.outputFieldsOverride ?? null,
    );
    return rawOutputFormat && schema ? { ...rawOutputFormat, schema } : rawOutputFormat;
  }, [rawOutputFormat, defaultAnalysisOption]);
  // docs/29 §8-1: 追加の観点 (あれば各画面の軸の候補に観点ごとの AI 解析の軸を足す)。
  const perspectives = useAnalysisPerspectives(district?.aiPostAnalyses, posts);
  const defaultPerspectiveLabel = defaultAnalysisOption?.label ?? null;

  // Sprint AV: 各 view を M5 displayRules で駆動。1 度だけ取得して props で配布。
  // Task D 動的化: scoreRules を追加配線 (AnalysisScoreView の軸 / 象限色)。
  const { matrixRules, mapRules, heatmapRules, networkRules, scoreRules } =
    useAnalysisDisplayConfig(district?.analysisDisplayConfigId ?? null);

  // Sprint X5c: ECW Insight / Idea 用に nickname を取得 (地区別 localStorage)
  const { nickname: localNickname } = useNickname(districtId);
  // Phase B: 旧 EcwUserSettings 等価 (ageGroup / gender) を IdeaWorkspace の display name に渡す。
  // old2 同様、管理者リンク経由 (override 有) の場合は membership 取得をスキップし空にする (L231-233)。
  const membershipFields = useMembershipUserFields(uid, districtId);
  // 2026-10-09: 参加登録の名前を使い、読めないときだけ端末内の保存値 (useResolvedNickname と同じ
  // 解決順。membership はすぐ上で購読しているのでその値を使う)。端末内の値だけだと気づき・処方箋の
  // 名前が空になり、画面・管理画面・カード出力で「匿名」になっていた。
  const nickname = (membershipFields.values.builtin.nickname || localNickname || '').trim();
  // docs/107 Wave1: 管理者プレビュー時は ?adminNickname= を表示名に優先 (old2 L231-233)。
  const effectiveNickname = adminNicknameOverride ?? nickname;
  // R-70 方針B: 端末引き継ぎ後も「自分の○○」(現 uid + 引き継ぎ元 linkedUids) を正しく扱う。
  const ageGroup = adminNicknameOverride
    ? undefined
    : formatMembershipProfileValue(membershipFields.values.builtin.ageGroup) || undefined;
  const gender = adminNicknameOverride
    ? undefined
    : formatMembershipProfileValue(membershipFields.values.builtin.gender) || undefined;
  // Phase B: idea title 重複検出と insight 一覧 (back モード起動時の materialize 用)。
  const ideasState = useEcwIdeas(districtId);
  const insightsState = useEcwInsights(districtId);
  // POC: 気づき/処方箋 outbox (オフライン送信保留)。AppHome 側にも別 instance あり (BroadcastChannel
  // で同期、posts/useOutbox と同じパターン)。
  const {
    state: ecwOutboxState,
    retryInsight: retryEcwInsight,
    retryIdea: retryEcwIdea,
  } = useEcwOutbox();
  const allIdeaTitles = useMemo(
    () => (ideasState.state.status === 'ready' ? ideasState.state.items.map((i) => i.title) : []),
    [ideasState.state],
  );
  // docs/107 Wave8: 気づきカード/詳細の「関連する処方箋」導線の有無判定用に処方箋一覧を配布。
  const ideaItems = useMemo(
    () => (ideasState.state.status === 'ready' ? ideasState.state.items : []),
    [ideasState.state],
  );
  // 投稿詳細の「この投稿から生まれた気づき・処方箋」と、id からの詳細解決に使う。
  const insightItems = useMemo(
    () => (insightsState.state.status === 'ready' ? insightsState.state.items : []),
    [insightsState.state],
  );

  // Sprint BG-DataSettings 28: 初期タブをマトリクスに (ユーザー要望、分析時の主入口)。
  const [activeTab, setActiveTab] = useState<AnalysisTab>('matrix');
  const [detailPost, setDetailPost] = useState<PostMinimal | null>(null);
  // 気づき / 処方箋の詳細 (2026-09-07)。ネットワーク図のノード選択と、投稿詳細内の
  // 「この投稿から生まれた気づき・処方箋」から開く。投稿詳細と同じ階層に置く。
  const [detailInsight, setDetailInsight] = useState<InsightMinimal | null>(null);
  const [detailIdea, setDetailIdea] = useState<IdeaMinimal | null>(null);

  /* === Phase B: ideaForm screen state machine ============================== */
  const [screen, setScreen] = useState<'main' | 'ideaForm'>('main');
  const [ideaFormMode, setIdeaFormMode] = useState<'front' | 'back'>('front');
  const [ideaFormPosts, setIdeaFormPosts] = useState<PostMinimal[]>([]);
  const [ideaFormInsights, setIdeaFormInsights] = useState<InsightMinimal[]>([]);
  const [ideaFormEmotion, setIdeaFormEmotion] = useState<string>('');
  const [ideaFormCategoryLabel, setIdeaFormCategoryLabel] = useState<string>('');
  // docs/107 Wave5: 分析(詳細選択) で active だった絞込フィルタ文字列 (old2 onIdeaGo filterList)。
  // front ワークスペースに「どの軸で選んだ投稿か」を文脈表示するため伝搬する。
  const [ideaFormFilterList, setIdeaFormFilterList] = useState<string[]>([]);
  /** insight 詳細から「関連処方箋を見る」 navigation 用 filter。 */
  const [relatedInsightFilter, setRelatedInsightFilter] = useState<InsightMinimal | null>(null);

  /**
   * Front workspace 起動 (List view / Insight view から呼ばれる)。
   * docs/107 Wave5: 分析(詳細選択) から渡る ProceedContext (emotion/categoryLabel/filterList) があれば
   * それを文脈として優先採用する (old2 onIdeaGo の引数伝搬)。無ければ従来通り先頭 post から導出。
   */
  const handleStartFrontWorkspace = useCallback(
    (postIds: string[], ctx?: ProceedContext) => {
      const picked = postIds.length > 0 ? filteredPosts.filter((p) => postIds.includes(p.id)) : [];
      setIdeaFormPosts(picked);
      setIdeaFormInsights([]);
      // 対話文脈: ProceedContext 優先、無ければ選択 post から mode 別に導出
      // (ECW=感情/カテゴリコード、防災=ホッと/ハッと/ヒント ラベル + 見つけたもの)。
      const derived = deriveDialogueContext(picked);
      setIdeaFormEmotion(ctx?.emotion || derived.emotion);
      setIdeaFormCategoryLabel(ctx?.categoryLabel || derived.categoryLabel);
      setIdeaFormFilterList(ctx?.filterList ?? []);
      setIdeaFormMode('front');
      setScreen('ideaForm');
    },
    [filteredPosts],
  );

  /** Back workspace 起動 (Insight view 「処方箋をつくる」 button から呼ばれる)。 */
  const handleStartBackWorkspace = useCallback(
    (insightsArg: InsightMinimal[]) => {
      setIdeaFormInsights(insightsArg);
      // back モード時の posts = 選択 insight の selectedPostIds を materialize
      const postIdSet = new Set<string>();
      insightsArg.forEach((i) => i.selectedPostIds.forEach((pid) => postIdSet.add(pid)));
      const materialized = filteredPosts.filter((p) => postIdSet.has(p.id));
      setIdeaFormPosts(materialized);
      // 起動時 emotion / category は選択投稿から mode 別に導出 (ECW=感情、防災=ホッと/ハッと/ヒント)。
      const derived = deriveDialogueContext(materialized);
      setIdeaFormEmotion(derived.emotion);
      setIdeaFormCategoryLabel(derived.categoryLabel);
      setIdeaFormFilterList([]);
      setIdeaFormMode('back');
      setScreen('ideaForm');
    },
    [filteredPosts],
  );

  /** Back workspace 起動 (prefilledInsightIds 経由、AnalysisIdeaView から)。 */
  const handleStartBackWorkspaceByIds = useCallback(
    (insightIds: string[]) => {
      const items = insightsState.state.status === 'ready' ? insightsState.state.items : [];
      const picked = items.filter((i) => insightIds.includes(i.id));
      if (picked.length === 0) return;
      handleStartBackWorkspace(picked);
    },
    [insightsState.state, handleStartBackWorkspace],
  );

  const handleSavedAndNavigate = useCallback((targetTab: string) => {
    setScreen('main');
    // 有効な tab key のみ反映
    if (
      targetTab === 'list' ||
      targetTab === 'analysis' ||
      targetTab === 'matrix' ||
      targetTab === 'score' ||
      targetTab === 'map' ||
      targetTab === 'heatmap' ||
      targetTab === 'insights' ||
      targetTab === 'ideas' ||
      targetTab === 'network'
    ) {
      setActiveTab(targetTab);
    }
  }, []);

  const handleShowRelatedIdeas = useCallback((insight: InsightMinimal) => {
    setRelatedInsightFilter(insight);
    setActiveTab('ideas');
  }, []);
  /* ======================================================================== */

  // Sprint BG-DataSettings 23: Matrix → List → InsightWS → InsightList → IdeaWS の handoff state。
  //   - listSelectedPostIds: 一覧タブの multi-select (Matrix からの絞込でも使う)
  //   - matrixFilterPostIds: Matrix cell → 一覧フィルタ (一覧の表示自体を絞込む)
  //   - insightPrefillPostIds: 一覧 → 気づき WS の handoff (1 度消費されたら null)
  //   - ideaPrefillInsightIds: 気づき一覧 → 処方箋 WS の handoff (1 度消費されたら null)
  const [listSelectedPostIds, setListSelectedPostIds] = useState<string[]>([]);
  const [matrixFilterPostIds, setMatrixFilterPostIds] = useState<string[] | null>(null);
  // docs/107 Wave2 / old2 AnalysisLayout.tsx:321-331,552 — 分析(詳細選択)タブの粗選択元タブ。
  // matrix/map のどちらから来たかを記録し、「← 戻る」で正しい元タブへ戻す。
  const [analysisSource, setAnalysisSource] = useState<'matrix' | 'map' | null>(null);
  // docs/107 Wave5: 分析(詳細選択) の card grid 既定軸用 context (マトリクスの縦横軸キー / 地図 source)。
  const [analysisContext, setAnalysisContext] = useState<AnalysisDetailContext | null>(null);
  const [insightPrefillPostIds, setInsightPrefillPostIds] = useState<string[] | undefined>(
    undefined,
  );
  const [ideaPrefillInsightIds, setIdeaPrefillInsightIds] = useState<string[] | undefined>(
    undefined,
  );

  // Matrix セル → 一覧タブで絞込表示。`matrixFilterPostIds` がある場合は一覧 post を絞り込む。
  const listPosts = useMemo(() => {
    if (!matrixFilterPostIds || matrixFilterPostIds.length === 0) return filteredPosts;
    const set = new Set(matrixFilterPostIds);
    return filteredPosts.filter((p) => set.has(p.id));
  }, [filteredPosts, matrixFilterPostIds]);

  // docs/107 Wave1 / old2 AnalysisLayout.tsx:599-606 — AI 認識物 (cls) 行クリック時、
  // 合成 PostMinimal (写真/同感数/解析設定を欠く degraded post) でなく実 PostMinimal を解決して
  // PostDetailModal に渡す。ID は "postId__clsN_M" 形式 (AnalysisMatrixView clsRowToPost)。
  const resolvedDetailPost = useMemo(() => {
    if (!detailPost) return null;
    if (!detailPost.id.includes('__cls')) return detailPost;
    const realId = detailPost.id.split('__cls')[0];
    return filteredPosts.find((p) => p.id === realId) ?? detailPost;
  }, [detailPost, filteredPosts]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  // Feature: 自分の投稿写真まとめてダウンロード modal (Drawer から開く、全画面で利用可能にするため)。
  const [photoDownloadOpen, setPhotoDownloadOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [deviceTransferOpen, setDeviceTransferOpen] = useState(false);
  const [categoryMasterOpen, setCategoryMasterOpen] = useState(false);
  const [gisSettingsOpen, setGisSettingsOpen] = useState(false);

  // 地区フォールバック根絶: 地区が未解決 (loading/error) の間は phase2-default を使わず、
  // 解決待ちプレースホルダを全画面表示する。以降 district は非 null に narrowing される。
  if (!district) {
    return <DistrictPendingScreen state={districtState} />;
  }

  // Phase B: screen='ideaForm' のとき、main body 全体を IdeaWorkspace に差し替える。
  if (screen === 'ideaForm') {
    return (
      <div className="flex flex-col h-dvh bg-white">
        <IdeaWorkspace
          districtId={district.id}
          mode={ideaFormMode}
          uid={uid}
          // 投稿詳細 (PostDetailModal) を投稿アプリと同一表示にするため owner 判定 + M5 config を渡す。
          selfUids={identity.allUids}
          analysisDisplayConfigId={district.analysisDisplayConfigId}
          nickname={effectiveNickname}
          ageGroup={ageGroup}
          gender={gender}
          selectedPosts={ideaFormPosts}
          postFields={postFields}
          categoryMasterRows={resolver.byCategoryCd}
          emotion={ideaFormEmotion}
          categoryLabel={ideaFormCategoryLabel}
          selectedInsights={ideaFormInsights}
          allIdeaTitles={allIdeaTitles}
          filterContext={ideaFormFilterList}
          aiDialogueSettingsId={district.aiDialogueSettingsId ?? null}
          onBack={() => setScreen('main')}
          onCreateInsight={async (input) => {
            // ユーザーのメインセッション (投稿の sessionId 供給源と同一) を気づきにも記録。
            await insightsState.createInsight({ ...input, sessionId: currentMainSessionId });
          }}
          onCreateIdea={async (input) => {
            await ideasState.createIdea({ ...input, sessionId: currentMainSessionId });
          }}
          onSavedAndNavigate={handleSavedAndNavigate}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-dvh bg-white">
      {/* ヘッダー (旧 AnalysisLayout header L412-486 踏襲) */}
      <header className="bg-primary-600 text-white px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 shrink-0 relative z-30">
        <DrawerToggleButton onClick={() => setDrawerOpen(true)} />
        <h1 className="font-bold text-sm truncate flex-1">{district.name} 分析ツール</h1>
        {postsState.status === 'loading' && <span className="text-xs text-white/70">読込中…</span>}
        {postsState.status === 'error' && (
          <span className="text-xs text-amber-200">読込エラー</span>
        )}
        {/* セッションフィルタ (docs/107 Wave1 / old2 AnalysisLayout.tsx:436-495 踏襲、brand→primary token) */}
        {allSessions.length > 0 && (
          <div className="relative" ref={sessionDropdownRef}>
            <button
              type="button"
              onClick={() => setSessionDropdownOpen((v) => !v)}
              className="text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-1 whitespace-nowrap min-h-touch"
            >
              セッション ({selectedSessionIds.size}/{allSessions.length}) ▼
            </button>
            {sessionDropdownOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setSessionDropdownOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-white text-slate-800 rounded-lg shadow-xl border border-slate-200 py-2 min-w-[220px] max-h-[60vh] overflow-y-auto">
                  <div className="px-3 py-1 flex gap-2 border-b border-slate-100 mb-1">
                    <button
                      type="button"
                      className="text-xs text-primary-600 font-bold hover:underline"
                      onClick={() => setSelectedSessionIds(new Set(allSessions.map((s) => s.id)))}
                    >
                      すべて選択
                    </button>
                    <button
                      type="button"
                      className="text-xs text-slate-500 hover:underline"
                      onClick={() => setSelectedSessionIds(new Set())}
                    >
                      すべて解除
                    </button>
                  </div>
                  {allSessions.map((s) => {
                    const checked = selectedSessionIds.has(s.id);
                    const isJoined = joinedSessionIds.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = new Set(selectedSessionIds);
                            if (checked) next.delete(s.id);
                            else next.add(s.id);
                            setSelectedSessionIds(next);
                          }}
                          className="w-3.5 h-3.5"
                        />
                        <span className={isJoined ? 'font-bold' : ''}>
                          {s.name}
                          {isJoined && <span className="ml-1 text-primary-600">(参加中)</span>}
                        </span>
                      </label>
                    );
                  })}
                  <div className="px-3 pt-1 border-t border-slate-100 mt-1 text-[10px] text-slate-400">
                    {filteredPosts.length}件 / 全{posts.length}件
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAuthenticated={auth.status === 'authenticated'}
        onShowAnnouncements={() => setHistoryOpen(true)}
        onShowSurveys={() => setSurveyOpen(true)}
        onShowVerification={() => setVerificationOpen(true)}
        onShowRecover={() => setRecoverOpen(true)}
        onShowSettings={() => setSettingsOpen(true)}
        onShowDeviceTransfer={() => setDeviceTransferOpen(true)}
        onShowCategoryMasterTable={() => setCategoryMasterOpen(true)}
        onShowGisSettings={() => setGisSettingsOpen(true)}
        onShowPhotoDownload={() => setPhotoDownloadOpen(true)}
      />

      {/* タブナビ (旧 L489-503 踏襲、brand-600 → primary-700 semantic token) */}
      <nav
        className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto shrink-0"
        role="tablist"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-3 sm:px-4 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors min-h-touch ${
              activeTab === t.key
                ? 'border-primary-600 text-primary-700 bg-white'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="mr-1">{t.icon}</span>
            {t.label}
          </button>
        ))}
        <div className="ml-auto px-3 py-2 text-[10px] text-slate-400 self-center">
          {filteredPosts.length} 投稿
          {!districtState && '（Phase 2 fallback）'}
        </div>
      </nav>

      {/* POC: 気づき/処方箋 outbox 一覧 (screen='main' のときのみ表示、tab content の上)。
          items 空のときは List 内で null を返す設計。 */}
      <EcwOutboxList
        insights={ecwOutboxState.insights}
        ideas={ecwOutboxState.ideas}
        onRetryInsight={retryEcwInsight}
        onRetryIdea={retryEcwIdea}
      />

      {/* メインコンテンツ。docs/107 Wave2 / old2 AnalysisLayout.tsx:636-653 TabPanel 踏襲:
          各タブは初回アクティブ化で lazy mount → 以後 display 切替で keep-alive (state 維持)。
          アクティブ化のたび analysisTabActivated イベントを発火し、map/heatmap/network が resize する。 */}
      <main className="flex-1 overflow-hidden relative">
        <TabPanel active={activeTab === 'matrix'} tabKey="matrix">
          <AnalysisMatrixView
            perspectives={perspectives}
            defaultPerspectiveLabel={defaultPerspectiveLabel}
            posts={filteredPosts}
            resolver={resolver}
            onSelectPost={setDetailPost}
            matrixRules={matrixRules}
            outputFormat={outputFormat}
            postFields={district.dataSettings?.postFields}
            onAnalyzeCell={(postIds, ctx) => {
              // docs/107 Wave5 (reviewer high): cls (AI認識物) モードのセルは `${postId}__cls${rank}`
              // 擬似 ID を含む (AnalysisMatrixView.clsRowToPost)。実 PostMinimal の id は base postId のため、
              // base に正規化 + dedup してから一覧フィルタへ渡す (resolvedDetailPost と同じ strip)。
              // 正規化しないと cls モード→分析(詳細) で listPosts が 0 件になり空タブ化する。
              const normalized = [...new Set(postIds.map((id) => id.split('__cls')[0] ?? id))];
              setMatrixFilterPostIds(normalized);
              setListSelectedPostIds([]);
              setAnalysisSource('matrix');
              // docs/107 Wave5: マトリクスの軸 + データソースを分析(詳細) の既定に伝搬。
              setAnalysisContext({
                source: 'matrix',
                vAxisKey: ctx?.vAxisKey,
                hAxisKey: ctx?.hAxisKey,
                dataSource: ctx?.dataSource,
              });
              // Phase 4: 粗選択の落とし先を『分析(詳細選択)』タブへ (旧 list→analysis)。
              setActiveTab('analysis');
            }}
          />
        </TabPanel>
        <TabPanel active={activeTab === 'map'} tabKey="map">
          <AnalysisMapView
            perspectives={perspectives}
            defaultPerspectiveLabel={defaultPerspectiveLabel}
            posts={filteredPosts}
            onSelectPost={setDetailPost}
            resolver={resolver}
            mapRules={mapRules}
            // 分析画面と同じ軸駆動フィルタ用 (custom 軸 / hiddenAxisKeys / includeEmpty)。
            matrixRules={matrixRules}
            mode={district.mode}
            districtId={district.id}
            pmtilesMeta={pmtilesMeta}
            // docs/105 地図軸 parity: matrix と同じ postField 軸候補 (rating-scale / number) を供給。
            postFields={district.dataSettings?.postFields}
            // 任意 mode (keikan/custom) の「分類 大分類」色軸を地図にも供給 (matrix と同じ M3)。
            outputFormat={outputFormat}
            // docs/22 GIS (要件4): 分析地図にも GIS レイヤーを重畳。
            gisEnabled={district.gisEnabled}
            // docs/24 手書きメモ (要件5): 地区設定で有効なら分析地図でも共有メモを表示/閲覧できる
            // (投稿地図 MapPage と同じ memo props。分析からの描画は特定セッションに紐付けない)。
            memoEnabled={district.mapDisplay?.memoEnabled === true}
            // 管理画面で地区に設定した色分け。選ぶとピン・凡例・ヒートマップが投稿アプリと同じ色になる。
            districtColorRule={districtColorRule}
            memoUid={uid}
            memoNickname={effectiveNickname}
            memoSessionId={null}
            // Phase 4: 地図から粗選択 → 分析(詳細選択)タブ (matrix の onAnalyzeCell と同じ落とし先)。
            onAnalyzeSelection={(postIds) => {
              setMatrixFilterPostIds(postIds);
              setListSelectedPostIds([]);
              setAnalysisSource('map');
              setAnalysisContext({ source: 'map' });
              setActiveTab('analysis');
            }}
          />
        </TabPanel>
        <TabPanel active={activeTab === 'analysis'} tabKey="analysis">
          {/* 画面の高さを「戻る導線」と一覧で分け合う縦 flex。
              これが無いと一覧 (h-full) が戻る導線の分だけ画面からはみ出し、TabPanel 側が
              スクロールして最下部の「気づきの記入へ」が画面外に隠れる。 */}
          <div className="flex flex-col h-full min-h-0">
            {/* 粗選択元 (matrix/map) への戻る導線 + 選択件数。
                戻る = 全件に戻す機能を兼ねるため「絞込解除」メニューは置かない (ユーザー指示)。
                選択元の種別文言は不要 (利用者は自分の操作を把握済) のため出さない。 */}
            {analysisSource && (
              <div className="shrink-0 px-3 sm:px-4 py-2 bg-sky-50 border-b border-sky-200 text-xs text-sky-800 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab(analysisSource)}
                  className="px-2 py-1 bg-white border border-sky-600 text-sky-700 rounded text-xs font-bold hover:bg-sky-100 min-h-touch"
                >
                  ← {analysisSource === 'map' ? '地図・図面に戻る' : 'マトリクスに戻る'}
                </button>
                <span>マトリクス/地図で選択した {listPosts.length} 件表示</span>
              </div>
            )}
            <div className="flex-1 min-h-0">
              <AnalysisListView
                perspectives={perspectives}
                defaultPerspectiveLabel={defaultPerspectiveLabel}
                posts={listPosts}
                resolver={resolver}
                onSelectPost={setDetailPost}
                selectedPostIds={listSelectedPostIds}
                onSelectionChange={setListSelectedPostIds}
                // 軸エンジン (マトリクスと共有): postField 由来軸 + master + M5 custom 軸 + cls 展開。
                postFields={district.dataSettings?.postFields}
                outputFormat={outputFormat}
                mode={district.mode}
                matrixRules={matrixRules}
                // マトリクス/地図由来の既定軸 + データソース (variable, 固定しない)。
                initialContext={analysisContext}
                // Phase 4: old2 流の一本道。「気づき作成へ」は insights タブを経由せず
                // front ワークスペースへ直行する (matrix/map→analysis→ideaForm)。
                // docs/107 Wave5: 絞込 context (emotion/categoryLabel/filterList) を ProceedContext で伝搬。
                onProceedToInsight={(ids, ctx) => handleStartFrontWorkspace(ids, ctx)}
              />
            </div>
          </div>
        </TabPanel>
        <TabPanel active={activeTab === 'score'} tabKey="score">
          <AnalysisScoreView
            posts={filteredPosts}
            onSelectPost={setDetailPost}
            scoreRules={scoreRules}
          />
        </TabPanel>
        <TabPanel active={activeTab === 'heatmap'} tabKey="heatmap">
          <AnalysisHeatmapView
            perspectives={perspectives}
            defaultPerspectiveLabel={defaultPerspectiveLabel}
            districtId={district.id}
            posts={filteredPosts}
            onSelectPost={setDetailPost}
            heatmapRules={heatmapRules}
            // 軸駆動フィルタ + 動的マーカー色 (マトリクスと同じ matrixAxisModel エンジン)。
            resolver={resolver}
            postFields={district.dataSettings?.postFields}
            matrixRules={matrixRules}
            outputFormat={outputFormat}
            // docs/107 オフライン残: offline 自動切替 / 地名 symbol 用に pmtiles メタを渡す。
            pmtilesMeta={pmtilesMeta}
            // docs/107 Wave7 / old2 HeatmapView.tsx:78-80 — 地区の投稿エリア中心を初期 center に。
            centerOverride={
              district.postAreaCenter
                ? {
                    lat: district.postAreaCenter.latitude,
                    lng: district.postAreaCenter.longitude,
                  }
                : undefined
            }
          />
        </TabPanel>
        <TabPanel active={activeTab === 'insights'} tabKey="insights">
          <AnalysisInsightView
            districtId={district.id}
            uid={uid}
            selfUids={identity.allUids}
            nickname={effectiveNickname}
            posts={filteredPosts}
            ideas={ideaItems}
            onSelectPost={setDetailPost}
            // ヘッダー右上のセッションフィルタを気づきにも適用 + カードにセッション名表示。
            sessions={allSessions}
            selectedSessionIds={selectedSessionIds}
            aiDialogueSettingsId={district.aiDialogueSettingsId ?? null}
            prefilledPostIds={insightPrefillPostIds}
            onPrefillConsumed={() => setInsightPrefillPostIds(undefined)}
            onProceedToIdea={(insight) => handleShowRelatedIdeas(insight)}
            onStartFrontWorkspace={handleStartFrontWorkspace}
            onStartBackWorkspace={handleStartBackWorkspace}
          />
        </TabPanel>
        <TabPanel active={activeTab === 'ideas'} tabKey="ideas">
          <AnalysisIdeaView
            districtId={district.id}
            uid={uid}
            selfUids={identity.allUids}
            nickname={effectiveNickname}
            posts={filteredPosts}
            onSelectPost={setDetailPost}
            // ヘッダー右上のセッションフィルタを処方箋にも適用 + カードにセッション名表示。
            sessions={allSessions}
            selectedSessionIds={selectedSessionIds}
            aiDialogueSettingsId={district.aiDialogueSettingsId ?? null}
            prefilledInsightIds={ideaPrefillInsightIds}
            onPrefillConsumed={() => setIdeaPrefillInsightIds(undefined)}
            onStartBackWorkspace={handleStartBackWorkspaceByIds}
            relatedInsightFilter={relatedInsightFilter}
            onClearRelatedInsightFilter={() => setRelatedInsightFilter(null)}
          />
        </TabPanel>
        <TabPanel active={activeTab === 'network'} tabKey="network">
          <AnalysisNetworkGraphView
            perspectives={perspectives}
            defaultPerspectiveLabel={defaultPerspectiveLabel}
            districtId={district.id}
            posts={filteredPosts}
            onSelectPost={setDetailPost}
            onSelectInsight={setDetailInsight}
            onSelectIdea={setDetailIdea}
            networkRules={networkRules}
            // 軸駆動フィルタ + 動的色分け軸 (マトリクスと同じ matrixAxisModel エンジン)。
            postFields={district.dataSettings?.postFields}
            matrixRules={matrixRules}
            outputFormat={outputFormat}
          />
        </TabPanel>
      </main>

      {/* 詳細 modal (Sprint X2 PostDetailModal 再利用、M5 displayConfigId 連動)。
          投稿者入力項目は PostDetailModal 内の PostDetailFields (visible 駆動・全ビュー連動) が表示する。 */}
      <PostDetailModal
        open={detailPost !== null}
        onClose={() => setDetailPost(null)}
        post={resolvedDetailPost}
        districtId={district.id}
        currentUid={uid}
        selfUids={identity.allUids}
        onDeleted={() => setDetailPost(null)}
        analysisDisplayConfigId={district.analysisDisplayConfigId}
        relatedInsights={insightItems}
        relatedIdeas={ideaItems}
        onSelectInsight={(id) => {
          const target = insightItems.find((i) => i.id === id);
          if (target) setDetailInsight(target);
        }}
        onSelectIdea={(id) => {
          const target = ideaItems.find((i) => i.id === id);
          if (target) setDetailIdea(target);
        }}
      />

      {/* 気づき / 処方箋の詳細 (2026-09-07)。ネットワーク図のノード選択・投稿詳細からの導線で開く。
          気づき詳細からは投稿詳細へ、投稿詳細からは気づき/処方箋へ、と 3 層を相互に辿れる。 */}
      <InsightDetailModal
        open={detailInsight !== null}
        onClose={() => setDetailInsight(null)}
        insight={detailInsight}
        posts={filteredPosts}
        ideas={ideaItems}
        districtId={district.id}
        sessionName={
          detailInsight?.sessionId ? (sessionNameById.get(detailInsight.sessionId) ?? null) : null
        }
        onSelectPost={setDetailPost}
        onShowRelatedIdeas={(insight) => {
          setDetailInsight(null);
          handleShowRelatedIdeas(insight);
        }}
      />
      <IdeaDetailModal
        open={detailIdea !== null}
        onClose={() => setDetailIdea(null)}
        idea={detailIdea}
        posts={filteredPosts}
        insights={insightItems}
        districtId={district.id}
        sessionName={
          detailIdea?.sessionId ? (sessionNameById.get(detailIdea.sessionId) ?? null) : null
        }
        onSelectPost={setDetailPost}
      />

      <PhotoDownloadModal
        open={photoDownloadOpen}
        onClose={() => setPhotoDownloadOpen(false)}
        districtId={district.id}
        uid={uid}
        posts={posts}
      />

      {/* Drawer trigger 経由で開く各 Modal (AppHome と同 pattern) */}
      {/* docs/22 GIS 設定 (要件5): gisEnabled 地区のみ Drawer に項目が出る。分析地図にも即時反映。 */}
      <GisSettingsModal
        open={gisSettingsOpen}
        onClose={() => setGisSettingsOpen(false)}
        districtId={district.id}
        enabled={district.gisEnabled === true}
        districtName={district.name}
      />
      <CategoryMasterTableModal
        open={categoryMasterOpen}
        onClose={() => setCategoryMasterOpen(false)}
        masters={resolver.masters}
        hasSettings={settingsId !== null}
      />
      <AnnouncementHistoryModal
        districtId={district.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <SurveyListModal open={surveyOpen} onClose={() => setSurveyOpen(false)} />
      {uid && (
        <VerificationListPage
          open={verificationOpen}
          onClose={() => setVerificationOpen(false)}
          districtId={district.id}
          uid={uid}
          currentSessionId={null}
        />
      )}
      {auth.status === 'authenticated' && (
        <>
          <SettingsModal
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            districtName={district.name}
            uid={auth.user.uid}
            districtToken={district.urlToken}
          />
          <RecoveryRecoverModal
            open={recoverOpen}
            onClose={() => setRecoverOpen(false)}
            districtName={district.name}
            districtToken={district.urlToken}
          />
          <DeviceTransferModal
            open={deviceTransferOpen}
            onClose={() => setDeviceTransferOpen(false)}
          />
        </>
      )}
    </div>
  );
}

/**
 * 分析ツールのタブパネル (docs/107 Wave2 / old2 AnalysisLayout.tsx:636-653 逐語移植)。
 * - 一度もアクティブになっていないタブは DOM 自体マウントしない (lazy mount)。
 * - 一度マウントしたタブは display: none で非表示にしつつ DOM を維持 (再マウントによる state 損失を防ぐ)。
 * - アクティブ化のたび `analysisTabActivated` カスタムイベントを発火し、各 View 側で
 *   maplibre `map.resize()` / canvas リサイズ / SVG 再計測を呼んでもらう (旧 leaflet invalidateSize 相当)。
 */
function TabPanel({
  active,
  tabKey,
  children,
}: {
  active: boolean;
  tabKey: string;
  children: ReactNode;
}) {
  const [hasBeenActive, setHasBeenActive] = useState(active);
  useEffect(() => {
    if (active) {
      setHasBeenActive(true);
      // CSS 反映後 (display:block 化後) に再描画させる
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('analysisTabActivated', { detail: { tabKey } }));
      });
    }
  }, [active, tabKey]);
  if (!hasBeenActive) return null;
  return (
    <div className="absolute inset-0 overflow-auto" style={{ display: active ? 'block' : 'none' }}>
      {children}
    </div>
  );
}
